import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useIntl } from 'react-intl'

import {
  DateSelectArg,
  DateSpanApi,
  DayHeaderContentArg,
  EventContentArg,
  EventDropArg,
  SlotLabelContentArg,
} from '@fullcalendar/core'
import interactionPlugin from '@fullcalendar/interaction'
import FullCalendar from '@fullcalendar/react'
import timeGridWeek from '@fullcalendar/timegrid'
import {
  addDays,
  addHours,
  addMilliseconds,
  addMinutes,
  differenceInDays,
  differenceInMinutes,
  isAfter,
  isBefore,
  parse,
  parseISO,
  startOfWeek,
} from 'date-fns'
import { format, getTimezoneOffset, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import styled, { useTheme } from 'styled-components/native'

import {
  buildDefaultEventTitle,
  eventsOverlap,
  getAdjustedEvent,
  getProportionalContentHeight,
  isWithinWorkingHours,
  projectWorkingHoursToDisplayTimeZone,
} from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/utils'
import {
  getTimeZoneName,
  toLocalISOString,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/timeZoneUtils'
import { BuiltEvent, EventExtendedProps } from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { AttentionIcon } from '../../atoms/icons/AttentionIcon'
import { Title } from '../../atoms/title/Title'
import { AppContext } from '../../context'
import { AlertPopUp } from '../../molecules/alertPopUp/AlertPopUp'
import { ProviderCalendarEvent } from '../../molecules/providerCalendarEvent/ProviderCalendarEvent'
import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { CalendarTimeZoneSelector } from '../calendarTimeZoneSelector/CalendarTimeZoneSelector'

const SLOT_WIDTH_WITH_TIMEZONE_SELECTOR = 78

export type ProviderBookableCalendarProps = {
  timeZone?: string
  /** Callback when timezone is changed via the selector. When provided, shows the timezone dropdown. */
  onTimeZoneChange?: (timeZone: string) => void
  /** Provider's configured timezone (used for reset option in dropdown) */
  providerTimeZone?: string
  onEventsChanged?: (events: BuiltEvent[]) => void
  disableEditing?: boolean
  initialEvents?: BuiltEvent[]
  modalAddedEvents?: BuiltEvent[]
  setEventToDelete?: (event: EventContentArg) => void
  setEventToEdit?: (event: EventContentArg) => void
  setRangeToAdd?: (
    timeRange: {
      start: string
      end: string
      startLocal: string
      endLocal: string
    } | null,
  ) => void
  setShowAddEventModal?: (showModal: boolean) => void
  showModalitySelection?: boolean
  supportsOnOffPlatformVideo?: boolean
  slotMaxTime?: string
  slotMinTime?: string
  hybridBookingMode?: boolean
  expandedWorkingHours?: boolean
  setSaveButtonDisabled?: (disabled: boolean) => void
  showExpandableWorkingHours?: boolean
  expandedStartTime?: string
  expandedEndTime?: string
  isNPSession?: boolean
  hiddenDays?: number[]
  flexibleAvailabilityWindows?: boolean
}

export type ProviderBookableCalendarRef = {
  reset: () => void
  decreaseBeyondBookingHoursEventCount: () => void
  changeWorkingHours: (workingHours: { startTime: string; endTime: string }) => void
}

const DayContentContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['4px'],
}))

const StyledAlertPopUp = styled(AlertPopUp)(({ theme }) => ({
  bottom: theme.spacing['24px'],
  left: theme.spacing['24px'],
  position: 'fixed',
  zIndex: 1,
}))

const INITIAL_EVENTS_ID = 'INITIAL_EVENTS'

const DEFAULT_HOURS_COUNT = 19 // matches default props: 4a.m. to 11p.m.
const DEFAULT_CONTENT_HEIGHT = 1280

export const ProviderBookableCalendar = forwardRef<ProviderBookableCalendarRef, ProviderBookableCalendarProps>(
  (
    {
      timeZone = 'America/Los_Angeles',
      onTimeZoneChange,
      providerTimeZone,
      onEventsChanged,
      disableEditing = false,
      initialEvents,
      modalAddedEvents,
      setEventToDelete = () => {},
      setEventToEdit = () => {},
      setRangeToAdd = () => {},
      setShowAddEventModal = () => {},
      showModalitySelection,
      supportsOnOffPlatformVideo,
      hybridBookingMode,
      expandedWorkingHours = false,
      setSaveButtonDisabled,
      showExpandableWorkingHours = false,
      slotMinTime = '04:00:00',
      slotMaxTime = '23:00:00',
      expandedStartTime = '04:00:00',
      expandedEndTime = '23:00:00',
      isNPSession,
      hiddenDays,
      flexibleAvailabilityWindows = false,
    },
    ref,
  ) => {
    const resolvedStartTime = expandedStartTime
    const resolvedEndTime = expandedEndTime === '00:00:00' ? '24:00:00' : expandedEndTime
    const contentHeight = getProportionalContentHeight(
      resolvedStartTime,
      resolvedEndTime,
      DEFAULT_HOURS_COUNT,
      DEFAULT_CONTENT_HEIGHT,
    )

    const calendarRef = useRef<FullCalendar>(null)
    const theme = useTheme() as ThemeType
    const { formatMessage } = useIntl()
    const { isExpandableWorkingHoursEnabled } = useContext(AppContext)
    const startingRange = useRef<{ startStr: string; endStr: string } | null>(null)
    const [beyondBookingHoursEventCount, setBeyondBookingHoursEventCount] = useState(0)
    const [showBeyondBookingHoursAlert, setShowBeyondBookingHoursAlert] = useState(false)
    const [axisElement, setAxisElement] = useState<Element | null>(null)

    // Update axis element content when timezone changes (for non-selector mode)
    useEffect(() => {
      if (axisElement && !onTimeZoneChange) {
        axisElement.textContent = getTimeZoneName({ timeZone, timeZoneName: 'short' }) ?? ''
      }
    }, [axisElement, onTimeZoneChange, timeZone])

    const workingHoursRef = useRef({
      startTime: slotMinTime,
      endTime: slotMaxTime,
    })

    const dayContentRenderer = useCallback(
      ({ date }: DayHeaderContentArg) => {
        const newDate = addMinutes(date, date.getTimezoneOffset())
        return (
          <DayContentContainer>
            <Title size={TitleSize.XSMALL} text={format(newDate, 'eeee', { timeZone })} level='2' />
          </DayContentContainer>
        )
      },
      [timeZone],
    )

    const slotLabelContent = useCallback(
      ({ text }: SlotLabelContentArg) => {
        // When timezone selector is enabled, use a wider container so FullCalendar
        // calculates a larger shrink width for the axis column
        const minWidth = onTimeZoneChange ? SLOT_WIDTH_WITH_TIMEZONE_SELECTOR : undefined
        return (
          <div style={{ minWidth }}>
            <BodyText color={theme.colors.textPrimary} size={BodyTextSize.SMALL} text={text} />
          </div>
        )
      },
      [onTimeZoneChange, theme.colors.textPrimary],
    )

    const getEventClassNames = useCallback(
      (args: EventContentArg) => (args.event.id === 'workingHours' ? ['lc-working-hours-bg'] : []),
      [],
    )

    const eventContent = useCallback(
      (args: EventContentArg) => {
        if (!args.event.startStr || !args.event.endStr) {
          return null
        }

        return (
          <ProviderCalendarEvent
            args={args}
            startingRange={startingRange}
            timeZone={timeZone}
            onDelete={(arg: EventContentArg) => setEventToDelete(arg)}
            onEdit={(arg: EventContentArg) => setEventToEdit(arg)}
            isTemplate
            showModalitySelection={showModalitySelection}
            supportsOnOffPlatformVideo={supportsOnOffPlatformVideo}
            flexibleAvailabilityWindows={flexibleAvailabilityWindows}
          />
        )
      },
      [
        supportsOnOffPlatformVideo,
        flexibleAvailabilityWindows,
        setEventToDelete,
        setEventToEdit,
        showModalitySelection,
        timeZone,
      ],
    )

    const getEventContent = useCallback(
      (args: EventContentArg) => (args.event.display === 'background' ? { domNodes: [] } : eventContent(args)),
      [eventContent],
    )

    const resolvedProviderTimeZone = providerTimeZone ?? timeZone

    const onSelect = useCallback(
      (event: DateSelectArg) => {
        if (!event.start || !event.end) {
          return
        }
        const adjustedEvent = getAdjustedEvent(event, startingRange.current, {
          skipRounding: flexibleAvailabilityWindows || isNPSession,
        })
        if (
          !isWithinWorkingHours({
            event: adjustedEvent,
            workingHours: {
              start: slotMinTime,
              end: slotMaxTime,
            },
            timeZone: resolvedProviderTimeZone,
            displayTimeZone: timeZone,
          })
        ) {
          calendarRef.current?.getApi().unselect()
          return
        }
        if (showModalitySelection || flexibleAvailabilityWindows || isNPSession) {
          // start/startLocal are "fake UTC"; see toFakeUTCISOString docs
          setRangeToAdd({
            start: adjustedEvent.start,
            end: adjustedEvent.end,
            startLocal: adjustedEvent.start,
            endLocal: adjustedEvent.end,
          })
          setShowAddEventModal(true)
        } else {
          let startTime = parseISO(adjustedEvent.start)
          const endTime = parseISO(adjustedEvent.end)
          const differenceHours = differenceInMinutes(endTime, startTime) / 60
          const numEvents = Math.ceil(differenceHours)
          for (let i = 0; i < numEvents; i++) {
            calendarRef.current?.getApi().addEvent({
              title: buildDefaultEventTitle(hybridBookingMode),
              start: startTime,
              end: addHours(startTime, 1),
              extendedProps: {
                lyraEventType: 'bookable',
              },
            })
            startTime = addHours(startTime, 1)
          }
        }
        calendarRef.current?.getApi().unselect()
      },
      [
        flexibleAvailabilityWindows,
        hybridBookingMode,
        isNPSession,
        resolvedProviderTimeZone,
        setRangeToAdd,
        setShowAddEventModal,
        showModalitySelection,
        slotMinTime,
        slotMaxTime,
        timeZone,
      ],
    )

    useEffect(() => {
      if (beyondBookingHoursEventCount === 0) {
        setSaveButtonDisabled?.(false)
        setShowBeyondBookingHoursAlert(false)
      }
    }, [beyondBookingHoursEventCount, setSaveButtonDisabled])

    const addWorkingHours = useCallback(
      (workingHours: { startTime: string; endTime: string }) => {
        const event = calendarRef.current?.getApi().getEventById('workingHours')
        event?.remove()
        const projected = onTimeZoneChange
          ? projectWorkingHoursToDisplayTimeZone(
              workingHours.startTime,
              workingHours.endTime,
              resolvedProviderTimeZone,
              timeZone,
            )
          : {
              startTime: workingHours.startTime,
              endTime: workingHours.endTime === '00:00:00' ? '24:00:00' : workingHours.endTime,
            }
        calendarRef.current?.getApi().addEvent({
          startTime: projected.startTime,
          endTime: projected.endTime,
          daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
          display: 'background',
          color: theme.colors.components.providerCalendar.event.background.background,
          id: 'workingHours',
        })
        calendarRef.current
          ?.getApi()
          .getEvents()
          .filter((event) => event.id !== 'workingHours')
          .forEach((event) => {
            const localStartTime = addMinutes(event.start ?? new Date(), event.start?.getTimezoneOffset() ?? 0)
            const localEndTime = addMinutes(event.end ?? new Date(), event.end?.getTimezoneOffset() ?? 0)
            const workingStartTime = parse(workingHours.startTime, 'HH:mm:ss', localStartTime)
            let workingEndTime = parse(workingHours.endTime, 'HH:mm:ss', localStartTime)
            if (isBefore(workingEndTime, workingStartTime)) {
              workingEndTime = addDays(workingEndTime, 1)
            }
            const outsideBookingHours =
              isBefore(localStartTime, workingStartTime) ||
              isAfter(localEndTime, workingEndTime) ||
              isAfter(localStartTime, workingEndTime)
            if (event?.extendedProps?.outsideBookingHours !== outsideBookingHours) {
              event.setExtendedProp('outsideBookingHours', outsideBookingHours)
              if (outsideBookingHours) {
                setBeyondBookingHoursEventCount((prev: number) => prev + 1)
              }
            }
          })
      },
      [
        onTimeZoneChange,
        resolvedProviderTimeZone,
        theme.colors.components.providerCalendar.event.background.background,
        timeZone,
      ],
    )

    useEffect(() => {
      if (!showExpandableWorkingHours || !isExpandableWorkingHoursEnabled) {
        return
      }
      if (expandedWorkingHours) {
        setSaveButtonDisabled?.(false)
        setBeyondBookingHoursEventCount(0)
        setShowBeyondBookingHoursAlert(false)
      } else if (beyondBookingHoursEventCount > 0) {
        setSaveButtonDisabled?.(true)
        setShowBeyondBookingHoursAlert(true)
      }
    }, [
      beyondBookingHoursEventCount,
      isExpandableWorkingHoursEnabled,
      expandedWorkingHours,
      showExpandableWorkingHours,
      setSaveButtonDisabled,
    ])

    useEffect(() => {
      modalAddedEvents?.forEach((modalAddedEvent) => {
        calendarRef.current?.getApi().addEvent({
          title: modalAddedEvent.title ?? buildDefaultEventTitle(hybridBookingMode),
          start: modalAddedEvent.startTime,
          end: modalAddedEvent.endTime,
          extendedProps: {
            lyraEventType: 'bookable',
            modality_availability: modalAddedEvent.modality,
            booking_type: modalAddedEvent.booking_type,
          },
          id: modalAddedEvent.startTime,
        })
      })
    }, [hybridBookingMode, modalAddedEvents])

    const onSelectAllow = useCallback(
      (args: DateSpanApi) => {
        if (
          !isWithinWorkingHours({
            event: { start: args.startStr, end: args.endStr },
            workingHours: { start: slotMinTime, end: slotMaxTime },
            timeZone: resolvedProviderTimeZone,
            displayTimeZone: timeZone,
          })
        ) {
          return false
        }

        const adjustedEvent = getAdjustedEvent(args, startingRange.current, {
          skipRounding: flexibleAvailabilityWindows || isNPSession,
        })
        return !calendarRef.current
          ?.getApi()
          .getEvents()
          .some(
            (event) =>
              event.display !== 'background' &&
              eventsOverlap(
                {
                  start: parseISO(event.startStr + 'Z'),
                  end: parseISO(event.endStr + 'Z'),
                },
                {
                  start: parseISO(adjustedEvent.start),
                  end: parseISO(adjustedEvent.end),
                },
              ),
          )
      },
      [flexibleAvailabilityWindows, isNPSession, resolvedProviderTimeZone, slotMaxTime, slotMinTime, timeZone],
    )

    const onEventChange = useCallback(() => {
      onEventsChanged?.(
        calendarRef.current
          ?.getApi()
          .getEvents()
          .filter((event) => event.display !== 'background')
          .map((event) => {
            const extendedProps = event.extendedProps as EventExtendedProps
            return {
              startTime: toLocalISOString(event.startStr, timeZone),
              endTime: toLocalISOString(event.endStr, timeZone),
              modality: extendedProps.modality_availability,
              title: event.title,
              booking_type: extendedProps.booking_type,
            }
          }) ?? [],
      )
    }, [onEventsChanged, timeZone])

    const convertDateToSameTimeInCurrentWeek = useCallback(
      (date: Date) => {
        const currentTime = new Date()
        const originalInZone = utcToZonedTime(date, timeZone)
        const targetInZone = utcToZonedTime(currentTime, timeZone)

        const startOfOriginalWeek = startOfWeek(originalInZone, {
          weekStartsOn: 0,
        })
        const startOfTargetWeek = startOfWeek(targetInZone, {
          weekStartsOn: 0,
        })

        const dayDifference = differenceInDays(originalInZone, startOfOriginalWeek)
        const targetDateWithSameDay = addDays(startOfTargetWeek, dayDifference)

        const originalTime = format(originalInZone, 'HH:mm:ss.SSS', {
          timeZone,
        })
        const targetDateWithSameTime = new Date(
          `${format(targetDateWithSameDay, 'yyyy-MM-dd', {
            timeZone,
          })}T${originalTime}`,
        )

        return zonedTimeToUtc(targetDateWithSameTime, timeZone)
      },
      [timeZone],
    )
    const resetEventsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const resetEvents = useCallback(() => {
      if (!calendarRef.current || !initialEvents) {
        return
      }
      const { startTime, endTime } = workingHoursRef.current
      // FullCalendar library uses flushSync when calling remove/addEventSource which will warn when called while already rendering
      // Fix found here: https://ah-mahir.medium.com/understanding-reacts-flushsync-warning-and-how-to-handle-it-with-settimeout-9442f6dd4ed9
      resetEventsTimeoutRef.current = setTimeout(() => {
        calendarRef.current?.getApi().getEventSourceById(INITIAL_EVENTS_ID)?.remove()
        calendarRef.current?.getApi().addEventSource({
          id: INITIAL_EVENTS_ID,
          events: [
            ...initialEvents.map((event) => {
              const startTime = convertDateToSameTimeInCurrentWeek(parseISO(event.startTime))
              const endTime = convertDateToSameTimeInCurrentWeek(parseISO(event.endTime))
              // Get the selected timezone's offset and add it to display correctly
              const tzOffsetMs = getTimezoneOffset(timeZone, startTime)
              return {
                title: buildDefaultEventTitle(hybridBookingMode),
                start: addMilliseconds(startTime, tzOffsetMs),
                end: addMilliseconds(endTime, tzOffsetMs),
                extendedProps: {
                  lyraEventType: 'bookable',
                  modality_availability: event.modality,
                  booking_type: event.booking_type,
                },
              }
            }),
            {
              ...(onTimeZoneChange
                ? projectWorkingHoursToDisplayTimeZone(startTime, endTime, resolvedProviderTimeZone, timeZone)
                : {
                    startTime,
                    endTime: endTime === '00:00:00' ? '24:00:00' : endTime,
                  }),
              daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
              display: 'background',
              color: theme.colors.components.providerCalendar.event.background.background,
              id: 'workingHours',
            },
          ],
        })
        onEventChange()
      }, 0)
    }, [
      convertDateToSameTimeInCurrentWeek,
      hybridBookingMode,
      initialEvents,
      onEventChange,
      onTimeZoneChange,
      resolvedProviderTimeZone,
      theme.colors.components.providerCalendar.event.background.background,
      timeZone,
    ])

    // Clear timeout on unmount to prevent memory leaks
    useEffect(() => {
      return () => {
        if (resetEventsTimeoutRef.current) {
          clearTimeout(resetEventsTimeoutRef.current)
        }
      }
    }, [])

    // Re-render events when initialEvents changes (e.g., after async API fetch completes)
    // Also re-runs when resetEvents changes (e.g., timezone change) to rebuild the workingHours band
    useEffect(() => {
      if (initialEvents) {
        resetEvents()
      }
    }, [initialEvents, resetEvents])

    useImperativeHandle(
      ref,
      () => ({
        reset: resetEvents,
        decreaseBeyondBookingHoursEventCount: () => {
          setBeyondBookingHoursEventCount((prev: number) => prev - 1)
        },
        changeWorkingHours: (workingHours: { startTime: string; endTime: string }) => {
          addWorkingHours(workingHours)
        },
      }),
      [addWorkingHours, resetEvents],
    )

    const eventAllow = useCallback(
      (span: DateSpanApi) => {
        return isWithinWorkingHours({
          event: { start: span.startStr, end: span.endStr },
          workingHours: { start: slotMinTime, end: slotMaxTime },
          timeZone: resolvedProviderTimeZone,
          displayTimeZone: timeZone,
        })
      },
      [resolvedProviderTimeZone, slotMaxTime, slotMinTime, timeZone],
    )

    const eventDrop = useCallback(
      (info: EventDropArg) => {
        if (info.event?.extendedProps?.outsideBookingHours) {
          info.event.setExtendedProp('outsideBookingHours', false)
          setBeyondBookingHoursEventCount((prev: number) => prev - 1)
        }
      },
      [setBeyondBookingHoursEventCount],
    )

    return (
      <>
        <FullCalendar
          ref={calendarRef}
          headerToolbar={false}
          plugins={[timeGridWeek, interactionPlugin]}
          initialView='timeGridWeek'
          allDaySlot={false}
          dayHeaderContent={dayContentRenderer}
          selectMirror
          slotLabelContent={slotLabelContent}
          eventColor='transparent'
          expandRows={true}
          contentHeight={contentHeight}
          stickyHeaderDates={true}
          selectable={!disableEditing}
          select={onSelect}
          timeZone={timeZone}
          eventClassNames={getEventClassNames}
          eventContent={getEventContent}
          eventOverlap={(stillEvent) => stillEvent.display === 'background'}
          eventChange={onEventChange}
          eventRemove={onEventChange}
          eventAdd={onEventChange}
          eventAllow={eventAllow}
          eventDrop={eventDrop}
          eventStartEditable={!disableEditing}
          eventMouseEnter={(args) => {
            args.event.setExtendedProp('hovered', true)
          }}
          eventMouseLeave={(args) => {
            args.event.setExtendedProp('hovered', false)
          }}
          selectAllow={onSelectAllow}
          slotMinTime={resolvedStartTime}
          slotMaxTime={resolvedEndTime}
          hiddenDays={hiddenDays}
          viewDidMount={({ el }) => {
            const axisEl = el?.querySelector('.fc-timegrid-axis-frame')
            if (axisEl) {
              setAxisElement(axisEl)
            }
          }}
          {...(flexibleAvailabilityWindows && { snapDuration: '00:15:00' })}
        />
        {/* Portal for timezone selector - renders into FullCalendar's axis element */}
        {axisElement &&
          onTimeZoneChange &&
          createPortal(
            <CalendarTimeZoneSelector
              selectedTimeZone={timeZone}
              providerTimeZone={providerTimeZone ?? timeZone}
              onTimeZoneSelected={onTimeZoneChange}
              testID={tID('ProviderBookableCalendar-timezone-selector')}
            />,
            axisElement,
          )}
        {showBeyondBookingHoursAlert && (
          <StyledAlertPopUp
            ariaLabel={'ProviderBookableCalendar-outsideWorkingHoursAlert'}
            icon={<AttentionIcon size={16} />}
            text={formatMessage(
              {
                defaultMessage: `{beyondBookingHoursEventCount} {beyondBookingHoursEventCount, plural,
                    one {spot falls}
                    other {spots fall}
                  } outside {hybridBookingMode, select, true {booking} other {working}} hours. Delete or move spots to continue.`,
                description: 'Popup message to let the user know they have spots outside of working hours',
              },
              { beyondBookingHoursEventCount, hybridBookingMode },
            )}
            onClose={() => setShowBeyondBookingHoursAlert(false)}
            secondaryButton={{
              onPress: () => {
                calendarRef.current
                  ?.getApi()
                  .getEvents()
                  .forEach((event) => {
                    if (event.extendedProps.outsideBookingHours) {
                      event.remove()
                    }
                  })
                setBeyondBookingHoursEventCount(0)
                setShowBeyondBookingHoursAlert(false)
                setSaveButtonDisabled?.(false)
              },
              text: formatMessage({
                defaultMessage: 'Delete outside spots',
                description: 'Button to close toast',
              }),
              testID: 'ProviderBookableCalendar-outsideWorkingHoursAlert',
            }}
          />
        )}
      </>
    )
  },
)
